import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type PendingNavigation =
  | {
      type: "POP";
    }
  | {
      pathname: string;
      replace?: boolean;
      state?: unknown;
      type?: string;
    };

type NavigationPromptRenderProps = {
  onCancel: () => void;
  onConfirm: () => void;
  show: boolean;
};

type NavigationPromptProps = {
  children?: (_props: NavigationPromptRenderProps) => React.ReactNode;
  when: boolean | (() => boolean);
};

const NavigationPrompt = ({ when, children }: NavigationPromptProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const shouldBlockRef = useRef(false);
  const navigationRef = useRef(navigate);

  // Update navigation ref when navigate changes
  useEffect(() => {
    navigationRef.current = navigate;
  }, [navigate]);

  const shouldBlock = typeof when === "function" ? when() : when;

  useEffect(() => {
    shouldBlockRef.current = shouldBlock;

    if (!shouldBlock) {
      return;
    }

    // Handle browser back/forward button using popstate
    const handlePopState = () => {
      if (shouldBlockRef.current) {
        // Prevent navigation and show prompt
        window.history.pushState(null, "", location.pathname + location.search);
        setShowPrompt(true);
        setPendingNavigation({ type: "POP" });
      }
    };

    // Handle beforeunload (page refresh/close)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    // Push current state to prevent immediate navigation
    window.history.pushState(null, "", location.pathname + location.search);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldBlock, location.pathname, location.search]);

  const handleConfirm = () => {
    if (pendingNavigation) {
      if (pendingNavigation.type === "POP") {
        // Navigate back
        navigationRef.current(-1);
      } else if (
        "pathname" in pendingNavigation &&
        pendingNavigation.pathname
      ) {
        navigationRef.current(pendingNavigation.pathname, {
          replace: pendingNavigation.replace,
          state: pendingNavigation.state,
        });
      }
    }
    setShowPrompt(false);
    setPendingNavigation(null);
    shouldBlockRef.current = false;
  };

  const handleCancel = () => {
    setShowPrompt(false);
    setPendingNavigation(null);
    // Restore the current state
    window.history.pushState(null, "", location.pathname + location.search);
  };
  if (typeof children === "function") {
    const childrenResult = children({
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      show: showPrompt,
    });
    // Ensure we return a valid React element
    return React.isValidElement(childrenResult) ? childrenResult : null;
  }

  return null;
};

export default NavigationPrompt;
